export type ContentSeedEntry = readonly [
  key: string,
  thai: string,
  romanization: string,
  english: string,
  dutch: string,
];

export interface LessonSeed {
  slug: string;
  emoji: string;
  title: {
    en: string;
    nl: string;
    th: string;
  };
  summary: string;
  vocabulary: readonly ContentSeedEntry[];
  sentences: readonly ContentSeedEntry[];
}

export const APPROVED_TOPIC_ORDER = [
  "greetings",
  "everyday-phrases",
  "people",
  "family",
  "questions",
  "numbers",
  "colors",
  "days",
  "time",
  "home",
  "food",
  "drinks",
  "restaurant",
  "common-verbs",
  "adjectives",
  "feelings",
  "clothing",
  "body-health",
  "animals",
  "nature",
  "weather",
  "hobbies",
  "places",
  "transport",
  "directions",
  "shopping",
  "convenience-store",
  "at-the-mall",
  "communication",
  "emergency",
] as const;

export const STARTER_30_SEEDS = [
  {
    slug: "everyday-phrases",
    emoji: "💬",
    title: {
      en: "Everyday Phrases",
      nl: "Alledaagse zinnen",
      th: "วลีในชีวิตประจำวัน",
    },
    summary: "Start with useful phrases for friendly everyday conversations.",
    vocabulary: [
      ["sorry", "ขอโทษ", "khǎw-thôot", "Sorry / Excuse me", "Sorry / Pardon"],
      ["do-not-know", "ไม่รู้", "mâi rúu", "I don't know", "Ik weet het niet"],
      [
        "not-sure",
        "ไม่แน่ใจ",
        "mâi nâe-jai",
        "I'm not sure",
        "Ik weet het niet zeker",
      ],
      ["can-do", "ได้", "dâai", "Sure / Can do / OK", "Ja / Dat kan / Oké"],
      [
        "cannot",
        "ไม่ได้",
        "mâi dâai",
        "Can't / Not possible",
        "Dat kan niet / Onmogelijk",
      ],
    ],
    sentences: [
      [
        "eaten-yet",
        "กินข้าวหรือยัง",
        "Gin khâao rěu yang?",
        "Have you eaten yet?",
        "Heb je al gegeten?",
      ],
      [
        "speak-little-thai",
        "ผมพูดภาษาไทยได้นิดหน่อยครับ",
        "Phǒm phûut phaa-sǎa Thai dâai nít-nòi khráp.",
        "I can speak a little Thai.",
        "Ik spreek een beetje Thais.",
      ],
    ],
  },
  {
    slug: "greetings",
    emoji: "👋",
    title: {
      en: "Greetings",
      nl: "Begroetingen",
      th: "คำทักทาย",
    },
    summary: "Say hello, ask how someone is, and thank people.",
    vocabulary: [
      ["hello", "สวัสดี", "sà-wàt-dee", "Hello / Goodbye", "Hallo / Tot ziens"],
      ["thank-you", "ขอบคุณ", "khàwp-khun", "Thank you", "Dank je wel"],
      [
        "how-are-you",
        "สบายดีไหม",
        "sà-baai-dee mǎi",
        "How are you?",
        "Hoe gaat het?",
      ],
      ["i-am-fine", "สบายดี", "sà-baai-dee", "I'm fine", "Het gaat goed"],
      [
        "nice-to-meet-you",
        "ยินดีที่ได้รู้จัก",
        "yin-dee thêe dâai rúu-jàk",
        "Nice to meet you",
        "Aangenaam kennis te maken",
      ],
      [
        "no-problem",
        "ไม่เป็นไร",
        "mâi bpen rai",
        "You're welcome / Never mind",
        "Graag gedaan / Geeft niet",
      ],
    ],
    sentences: [
      [
        "hello-name-john",
        "สวัสดีครับ ผมชื่อจอห์น",
        "Sà-wàt-dee khráp. Phǒm chêu John.",
        "Hello. My name is John.",
        "Hallo. Ik heet John.",
      ],
      [
        "thank-you-very-much",
        "ขอบคุณมากครับ",
        "Khàwp-khun mâak khráp.",
        "Thank you very much.",
        "Heel erg bedankt.",
      ],
    ],
  },
  {
    slug: "questions",
    emoji: "❓",
    title: {
      en: "Questions",
      nl: "Vragen",
      th: "คำถาม",
    },
    summary: "Answer yes or no, and ask what something is or where it is.",
    vocabulary: [
      ["yes-correct", "ใช่", "châi", "Yes / Correct", "Ja / Dat klopt"],
      ["not-correct", "ไม่ใช่", "mâi châi", "No / Incorrect", "Nee / Dat klopt niet"],
      ["not", "ไม่", "mâi", "No / Not", "Nee / Niet"],
      ["where", "ที่ไหน", "thêe-nǎi", "Where", "Waar"],
      ["what", "อะไร", "à-rai", "What", "Wat"],
    ],
    sentences: [
      [
        "speak-thai",
        "คุณพูดภาษาไทยได้ไหม",
        "Khun phûut phaa-sǎa Thai dâai mǎi?",
        "Can you speak Thai?",
        "Spreekt u Thais?",
      ],
      ["what-is-this", "นี่คืออะไร", "Nêe khue à-rai?", "What is this?", "Wat is dit?"],
    ],
  },
  {
    slug: "numbers",
    emoji: "🔢",
    title: {
      en: "Numbers",
      nl: "Getallen",
      th: "ตัวเลข",
    },
    summary: "Count from one to five and use numbers in short requests.",
    vocabulary: [
      ["one", "หนึ่ง", "nèung", "One", "Eén"],
      ["two", "สอง", "sǎwng", "Two", "Twee"],
      ["three", "สาม", "sǎam", "Three", "Drie"],
      ["four", "สี่", "sèe", "Four", "Vier"],
      ["five", "ห้า", "hâa", "Five", "Vijf"],
    ],
    sentences: [
      [
        "two-siblings",
        "ผมมีพี่น้องสองคน",
        "Phǒm mee phêe-náwng sǎwng khon.",
        "I have two siblings.",
        "Ik heb twee broers of zussen.",
      ],
      [
        "table-for-four",
        "ขอโต๊ะสำหรับสี่คนครับ",
        "Khǎw dtó sǎm-ràp sèe khon khráp.",
        "A table for four, please.",
        "Een tafel voor vier, alstublieft.",
      ],
    ],
  },
  {
    slug: "food",
    emoji: "🍜",
    title: {
      en: "Food",
      nl: "Eten",
      th: "อาหาร",
    },
    summary: "Describe basic tastes and ask for water at the table.",
    vocabulary: [
      ["food", "อาหาร", "aa-hǎan", "Food", "Eten"],
      ["water", "น้ำ", "náam", "Water / Liquid", "Water / Vloeistof"],
      ["delicious", "อร่อย", "à-ròi", "Delicious", "Lekker"],
      ["spicy", "เผ็ด", "phèt", "Spicy", "Pittig"],
      ["sweet", "หวาน", "wǎan", "Sweet", "Zoet"],
    ],
    sentences: [
      [
        "glass-of-water",
        "ขอน้ำหนึ่งแก้วครับ",
        "Khǎw náam nèung gâew khráp.",
        "May I have a glass of water?",
        "Mag ik een glas water?",
      ],
      [
        "food-delicious",
        "อาหารอร่อยมากครับ",
        "Aa-hǎan à-ròi mâak khráp.",
        "The food is delicious.",
        "Het eten is erg lekker.",
      ],
    ],
  },
  {
    slug: "people",
    emoji: "👤",
    title: {
      en: "People",
      nl: "Mensen",
      th: "ผู้คน",
    },
    summary: "Meet common Thai pronouns and ask where someone comes from.",
    vocabulary: [
      ["i-male", "ผม", "phǒm", "I / me (man)", "ik / mij (man)"],
      [
        "i-female-informal",
        "ฉัน",
        "chǎn",
        "I / me (woman)",
        "ik / mij (vrouw)",
      ],
      ["you", "คุณ", "khun", "You / Mr. / Ms.", "U / Jij / Meneer / Mevrouw"],
      ["they", "เขา", "khǎo", "He / She / They", "Hij / Zij / Diegene"],
      ["we", "เรา", "rao", "We / Us", "Wij / Ons"],
    ],
    sentences: [
      [
        "i-am-english",
        "ผมเป็นคนอังกฤษครับ",
        "Phǒm bpen khon Ang-grìt khráp.",
        "I am English.",
        "Ik ben Engels.",
      ],
      [
        "where-from",
        "คุณมาจากไหนครับ",
        "Khun maa jàak nǎi khráp?",
        "Where are you from?",
        "Waar komt u vandaan?",
      ],
    ],
  },
  {
    slug: "family",
    emoji: "👨‍👩‍👧",
    title: {
      en: "Family",
      nl: "Familie",
      th: "ครอบครัว",
    },
    summary: "Name close family members and talk about your household.",
    vocabulary: [
      ["father", "พ่อ", "phâw", "Father", "Vader"],
      ["mother", "แม่", "mâe", "Mother", "Moeder"],
      ["older-sibling", "พี่", "phêe", "Older sibling", "Oudere broer of zus"],
      ["younger-sibling", "น้อง", "náwng", "Younger sibling", "Jongere broer of zus"],
      ["family", "ครอบครัว", "khrâwp-khrua", "Family", "Familie"],
    ],
    sentences: [
      [
        "father-somchai",
        "พ่อของผมชื่อสมชาย",
        "Phâw khǎwng phǒm chêu Sǒm-chaai.",
        "My father's name is Somchai.",
        "Mijn vader heet Somchai.",
      ],
      [
        "mother-cooks",
        "แม่ชอบทำอาหารครับ",
        "Mâe châwp tham aa-hǎan khráp.",
        "My mother likes to cook.",
        "Mijn moeder kookt graag.",
      ],
    ],
  },
  {
    slug: "places",
    emoji: "📍",
    title: {
      en: "Places",
      nl: "Plaatsen",
      th: "สถานที่",
    },
    summary: "Recognize useful places around town and ask where they are.",
    vocabulary: [
      ["home", "บ้าน", "bâan", "House / Home", "Huis / Thuis"],
      ["restaurant", "ร้านอาหาร", "ráan aa-hǎan", "Restaurant", "Restaurant"],
      ["market", "ตลาด", "dtà-làat", "Market", "Markt"],
      ["hospital", "โรงพยาบาล", "roong phá-yaa-baan", "Hospital", "Ziekenhuis"],
      ["airport", "สนามบิน", "sà-nǎam bin", "Airport", "Luchthaven"],
    ],
    sentences: [
      [
        "at-home",
        "ผมอยู่ที่บ้านครับ",
        "Phǒm yùu thêe bâan khráp.",
        "I am at home.",
        "Ik ben thuis.",
      ],
      [
        "where-restaurant",
        "ร้านอาหารอยู่ที่ไหนครับ",
        "Ráan aa-hǎan yùu thêe-nǎi khráp?",
        "Where is the restaurant?",
        "Waar is het restaurant?",
      ],
    ],
  },
  {
    slug: "transport",
    emoji: "🚕",
    title: {
      en: "Transport",
      nl: "Vervoer",
      th: "การเดินทาง",
    },
    summary: "Identify common transport and make a simple taxi request.",
    vocabulary: [
      ["vehicle", "รถ", "rót", "Car / Vehicle", "Auto / Voertuig"],
      ["taxi", "รถแท็กซี่", "rót tháek-sêe", "Taxi", "Taxi"],
      ["skytrain", "รถไฟฟ้า", "rót-fai-fáa", "Skytrain (BTS)", "Luchtmetro (BTS)"],
      ["boat", "เรือ", "ruea", "Boat", "Boot"],
      ["go", "ไป", "bpai", "To go", "Gaan"],
    ],
    sentences: [
      [
        "request-taxi",
        "ขอรถแท็กซี่หน่อยครับ",
        "Khǎw rót tháek-sêe nòi khráp.",
        "Could I get a taxi, please?",
        "Een taxi, alstublieft.",
      ],
      [
        "go-airport",
        "ไปสนามบินครับ",
        "Bpai sà-nǎam bin khráp.",
        "Go to the airport, please.",
        "Naar de luchthaven, alstublieft.",
      ],
    ],
  },
  {
    slug: "colors",
    emoji: "🎨",
    title: {
      en: "Colors",
      nl: "Kleuren",
      th: "สี",
    },
    summary: "Learn five core colors and use them to describe familiar objects.",
    vocabulary: [
      ["color", "สี", "sěe", "Color", "Kleur"],
      ["red", "สีแดง", "sěe daeng", "Red", "Rood"],
      ["blue", "สีน้ำเงิน", "sěe náam-ngern", "Blue", "Blauw"],
      ["green", "สีเขียว", "sěe khǐao", "Green", "Groen"],
      ["yellow", "สีเหลือง", "sěe lěuang", "Yellow", "Geel"],
    ],
    sentences: [
      [
        "red-shirt",
        "เสื้อสีแดงสวยมาก",
        "Sêua sěe daeng sǔay mâak.",
        "The red shirt is very beautiful.",
        "Het rode shirt is erg mooi.",
      ],
      [
        "my-car-white",
        "รถของผมสีขาวครับ",
        "Rót khǎwng phǒm sěe khǎao khráp.",
        "My car is white.",
        "Mijn auto is wit.",
      ],
    ],
  },
  {
    slug: "time",
    emoji: "🕐",
    title: {
      en: "Time",
      nl: "Tijd",
      th: "เวลา",
    },
    summary: "Name parts of the day and ask or tell the time.",
    vocabulary: [
      ["time", "เวลา", "wee-laa", "Time", "Tijd"],
      ["hour", "ชั่วโมง", "chûa-moong", "Hour", "Uur"],
      ["minute", "นาที", "naa-thee", "Minute", "Minuut"],
      ["morning", "เช้า", "cháao", "Morning", "Ochtend"],
      ["midday", "กลางวัน", "glaang-wan", "Midday / Afternoon", "Middag"],
    ],
    sentences: [
      [
        "what-time",
        "ตอนนี้กี่โมงครับ",
        "Dtawn-née gèe moong khráp?",
        "What time is it now?",
        "Hoe laat is het nu?",
      ],
      [
        "appointment-three",
        "นัดบ่ายสามโมงครับ",
        "Nát bàai sǎam moong khráp.",
        "The appointment is at 3 p.m.",
        "De afspraak is om drie uur 's middags.",
      ],
    ],
  },
  {
    slug: "days",
    emoji: "📅",
    title: {
      en: "Days",
      nl: "Dagen",
      th: "วัน",
    },
    summary: "Start naming weekdays and ask which day it is.",
    vocabulary: [
      ["day", "วัน", "wan", "Day", "Dag"],
      ["monday", "วันจันทร์", "wan jan", "Monday", "Maandag"],
      ["tuesday", "วันอังคาร", "wan ang-khaan", "Tuesday", "Dinsdag"],
      ["wednesday", "วันพุธ", "wan phút", "Wednesday", "Woensdag"],
      ["thursday", "วันพฤหัสบดี", "wan pá-rué-hàt", "Thursday", "Donderdag"],
    ],
    sentences: [
      [
        "what-day",
        "วันนี้วันอะไรครับ",
        "Wan-née wan à-rai khráp?",
        "What day is it today?",
        "Welke dag is het vandaag?",
      ],
      [
        "meet-next-week",
        "สัปดาห์หน้าเจอกันนะครับ",
        "Sàp-daa nâa jer gan ná khráp.",
        "Let's meet next week.",
        "Zullen we volgende week afspreken?",
      ],
    ],
  },
  {
    slug: "weather",
    emoji: "⛅",
    title: {
      en: "Weather",
      nl: "Het weer",
      th: "สภาพอากาศ",
    },
    summary: "Describe hot, cold, rainy, and sunny weather.",
    vocabulary: [
      ["weather", "อากาศ", "aa-gàat", "Weather / Air", "Weer / Lucht"],
      ["hot", "ร้อน", "ráwn", "Hot", "Heet"],
      ["cold", "หนาว", "nǎao", "Cold", "Koud"],
      ["rain", "ฝน", "fǒn", "Rain", "Regen"],
      ["sunshine", "แดด", "dàet", "Sunshine / Sun", "Zonlicht / Zon"],
    ],
    sentences: [
      [
        "hot-today",
        "วันนี้อากาศร้อนมากครับ",
        "Wan-née aa-gàat ráwn mâak khráp.",
        "The weather is very hot today.",
        "Het is vandaag erg warm.",
      ],
      [
        "rain-today",
        "ฝนตกไหมวันนี้",
        "Fǒn dtòk mǎi wan-née?",
        "Is it raining today?",
        "Regent het vandaag?",
      ],
    ],
  },
  {
    slug: "shopping",
    emoji: "🛍️",
    title: {
      en: "Shopping",
      nl: "Winkelen",
      th: "การซื้อของ",
    },
    summary: "Ask about prices and recognize common shopping words.",
    vocabulary: [
      ["buy", "ซื้อ", "séu", "Buy", "Kopen"],
      ["sell", "ขาย", "khǎai", "Sell", "Verkopen"],
      ["price", "ราคา", "raa-khaa", "Price", "Prijs"],
      ["expensive", "แพง", "phaeng", "Expensive", "Duur"],
      ["cheap", "ถูก", "thùuk", "Cheap", "Goedkoop"],
    ],
    sentences: [
      [
        "how-much",
        "ราคาเท่าไหร่ครับ",
        "Raa-khaa thâo-rài khráp?",
        "How much does it cost?",
        "Hoeveel kost het?",
      ],
      [
        "ask-discount",
        "แพงไปหน่อย ลดได้ไหมครับ",
        "Phaeng bpai nòi, lót dâai mǎi khráp?",
        "It's a bit expensive. Can you give a discount?",
        "Het is wat duur. Kunt u korting geven?",
      ],
    ],
  },
  {
    slug: "body-health",
    emoji: "🏥",
    title: {
      en: "Body & Health",
      nl: "Lichaam en gezondheid",
      th: "ร่างกายและสุขภาพ",
    },
    summary: "Name basic body parts and explain two common symptoms.",
    vocabulary: [
      ["head", "หัว", "hǔa", "Head", "Hoofd"],
      ["eye", "ตา", "dtaa", "Eye", "Oog"],
      ["ear", "หู", "hǔu", "Ear", "Oor"],
      ["nose", "จมูก", "jà-mùuk", "Nose", "Neus"],
      ["mouth", "ปาก", "bpàak", "Mouth", "Mond"],
    ],
    sentences: [
      [
        "headache",
        "ผมปวดหัวครับ",
        "Phǒm bpùat hǔa khráp.",
        "I have a headache.",
        "Ik heb hoofdpijn.",
      ],
      [
        "fever",
        "ฉันเป็นไข้ค่ะ",
        "Chǎn bpen khâi khâ.",
        "I have a fever.",
        "Ik heb koorts.",
      ],
    ],
  },
  {
    slug: "feelings",
    emoji: "😊",
    title: {
      en: "Feelings",
      nl: "Gevoelens",
      th: "ความรู้สึก",
    },
    summary: "Name five feelings and say how you feel.",
    vocabulary: [
      ["happy", "ดีใจ", "dee-jai", "Happy / Glad", "Blij"],
      ["sad", "เสียใจ", "sǐa-jai", "Sad / Sorry", "Verdrietig / Het spijt me"],
      ["angry", "โกรธ", "gròot", "Angry", "Boos"],
      ["afraid", "กลัว", "glua", "Scared / Afraid", "Bang"],
      ["tired", "เหนื่อย", "nèuay", "Tired", "Moe"],
    ],
    sentences: [
      [
        "glad-meet",
        "ฉันดีใจที่ได้พบคุณ",
        "Chǎn dee-jai thêe dâai phóp khun.",
        "I'm glad to meet you.",
        "Ik ben blij u te ontmoeten.",
      ],
      [
        "very-tired",
        "ผมเหนื่อยมากครับ",
        "Phǒm nèuay mâak khráp.",
        "I am very tired.",
        "Ik ben erg moe.",
      ],
    ],
  },
  {
    slug: "directions",
    emoji: "🧭",
    title: {
      en: "Directions",
      nl: "De weg",
      th: "การบอกทาง",
    },
    summary: "Understand simple directions for turning and going straight.",
    vocabulary: [
      ["left", "ซ้าย", "sáai", "Left", "Links"],
      ["right", "ขวา", "khwǎa", "Right", "Rechts"],
      ["straight", "ตรงไป", "dtrong bpai", "Straight ahead", "Rechtdoor"],
      ["turn", "เลี้ยว", "líao", "Turn", "Afslaan"],
      ["near", "ใกล้", "glâi", "Near / Close", "Dichtbij"],
    ],
    sentences: [
      [
        "left-intersection",
        "เลี้ยวซ้ายที่สี่แยกครับ",
        "Líao sáai thêe sèe-yâek khráp.",
        "Turn left at the intersection.",
        "Sla linksaf bij de kruising.",
      ],
      [
        "straight-right",
        "ตรงไปแล้วเลี้ยวขวาครับ",
        "Dtrong bpai láeo líao khwǎa khráp.",
        "Go straight, then turn right.",
        "Ga rechtdoor en sla daarna rechtsaf.",
      ],
    ],
  },
  {
    slug: "home",
    emoji: "🏠",
    title: {
      en: "Home",
      nl: "Thuis",
      th: "บ้าน",
    },
    summary: "Name rooms at home and describe where they are.",
    vocabulary: [
      ["bedroom", "ห้องนอน", "hâwng-nawn", "Bedroom", "Slaapkamer"],
      ["kitchen", "ห้องครัว", "hâwng-khrua", "Kitchen", "Keuken"],
      ["living-room", "ห้องนั่งเล่น", "hâwng-nâng-lên", "Living room", "Woonkamer"],
      ["bathroom", "ห้องน้ำ", "hâwng-náam", "Bathroom", "Badkamer"],
      ["door", "ประตู", "bprà-dtuu", "Door", "Deur"],
    ],
    sentences: [
      [
        "bedroom-second-floor",
        "ห้องนอนอยู่ชั้นสองครับ",
        "Hâwng-nawn yùu chán sǎwng khráp.",
        "The bedroom is on the second floor.",
        "De slaapkamer is op de tweede verdieping.",
      ],
      [
        "kitchen-back",
        "ห้องครัวอยู่ข้างหลังครับ",
        "Hâwng-khrua yùu khâang lǎng khráp.",
        "The kitchen is at the back.",
        "De keuken is achterin.",
      ],
    ],
  },
  {
    slug: "clothing",
    emoji: "👕",
    title: {
      en: "Clothing",
      nl: "Kleding",
      th: "เสื้อผ้า",
    },
    summary: "Name everyday clothing and ask about size or trying something on.",
    vocabulary: [
      ["shirt", "เสื้อ", "sêua", "Shirt / Top", "Shirt / Bovenstuk"],
      ["trousers", "กางเกง", "gaang-geeng", "Pants / Trousers", "Broek"],
      ["skirt", "กระโปรง", "grà-bproong", "Skirt", "Rok"],
      ["shoes", "รองเท้า", "rawng-tháao", "Shoes", "Schoenen"],
      ["socks", "ถุงเท้า", "thǔng-tháao", "Socks", "Sokken"],
    ],
    sentences: [
      [
        "shoe-size",
        "รองเท้าใส่ขนาดอะไรครับ",
        "Rawng-tháao sài khà-nàat à-rai khráp?",
        "What shoe size do you wear?",
        "Welke schoenmaat heeft u?",
      ],
      [
        "try-shirt",
        "ขอลองเสื้อตัวนี้ได้ไหมครับ",
        "Khǎw lawng sêua dtua née dâai mǎi khráp?",
        "May I try on this shirt?",
        "Mag ik dit shirt passen?",
      ],
    ],
  },
  {
    slug: "common-verbs",
    emoji: "⚡",
    title: {
      en: "Common Verbs",
      nl: "Veelgebruikte werkwoorden",
      th: "คำกริยาที่ใช้บ่อย",
    },
    summary: "Practice five high-frequency actions in short sentences.",
    vocabulary: [
      ["eat", "กิน", "gin", "Eat", "Eten"],
      ["drink", "ดื่ม", "dèum", "Drink", "Drinken"],
      ["sleep", "นอน", "nawn", "Sleep", "Slapen"],
      ["read", "อ่าน", "àan", "Read", "Lezen"],
      ["write", "เขียน", "khǐan", "Write", "Schrijven"],
    ],
    sentences: [
      [
        "rice-every-morning",
        "ผมกินข้าวทุกเช้าครับ",
        "Phǒm gin khâao thúk cháao khráp.",
        "I eat rice every morning.",
        "Ik eet elke ochtend rijst.",
      ],
      [
        "likes-reading",
        "เธอชอบอ่านหนังสือ",
        "Thoe châwp àan nǎng-sěu.",
        "She likes to read books.",
        "Zij leest graag boeken.",
      ],
    ],
  },
  {
    slug: "adjectives",
    emoji: "✨",
    title: {
      en: "Adjectives",
      nl: "Bijvoeglijke naamwoorden",
      th: "คำคุณศัพท์",
    },
    summary: "Use common adjectives to describe language and familiar places.",
    vocabulary: [
      ["good", "ดี", "dee", "Good", "Goed"],
      ["not-good", "ไม่ดี", "mâi dee", "Bad / Not good", "Slecht / Niet goed"],
      ["new", "ใหม่", "mài", "New", "Nieuw"],
      ["old", "เก่า", "gào", "Old", "Oud"],
      ["big", "ใหญ่", "yài", "Big / Large", "Groot"],
    ],
    sentences: [
      [
        "thai-difficult",
        "ภาษาไทยยากไหมครับ",
        "Phaa-sǎa Thai yâak mǎi khráp?",
        "Is Thai difficult?",
        "Is het Thais moeilijk?",
      ],
      [
        "house-big",
        "บ้านของเขาใหญ่มากครับ",
        "Bâan khǎwng khǎo yài mâak khráp.",
        "Their house is very big.",
        "Hun huis is erg groot.",
      ],
    ],
  },
  {
    slug: "animals",
    emoji: "🐘",
    title: {
      en: "Animals",
      nl: "Dieren",
      th: "สัตว์",
    },
    summary: "Meet five familiar animals and use them in simple facts.",
    vocabulary: [
      ["dog", "สุนัข", "sù-nák", "Dog", "Hond"],
      ["cat", "แมว", "maeo", "Cat", "Kat"],
      ["elephant", "ช้าง", "cháang", "Elephant", "Olifant"],
      ["monkey", "ลิง", "ling", "Monkey", "Aap"],
      ["bird", "นก", "nók", "Bird", "Vogel"],
    ],
    sentences: [
      [
        "two-cats",
        "ฉันเลี้ยงแมวสองตัว",
        "Chǎn líang maeo sǎwng dtua.",
        "I have two cats.",
        "Ik heb twee katten.",
      ],
      [
        "elephant-symbol",
        "ช้างเป็นสัญลักษณ์ของไทย",
        "Cháang bpen sǎn-yá-lák khǎwng Thai.",
        "The elephant is a symbol of Thailand.",
        "De olifant is een symbool van Thailand.",
      ],
    ],
  },
  {
    slug: "nature",
    emoji: "🌿",
    title: {
      en: "Nature",
      nl: "Natuur",
      th: "ธรรมชาติ",
    },
    summary: "Name natural places and describe a favorite outdoor scene.",
    vocabulary: [
      ["sea", "ทะเล", "thá-lee", "Sea / Ocean", "Zee / Oceaan"],
      ["mountain", "ภูเขา", "phuu-khǎo", "Mountain", "Berg"],
      ["river", "แม่น้ำ", "mâe-náam", "River", "Rivier"],
      ["forest", "ป่า", "bpàa", "Forest / Jungle", "Bos / Jungle"],
      ["flower", "ดอกไม้", "dàwk-mái", "Flower", "Bloem"],
    ],
    sentences: [
      [
        "sea-thailand",
        "ทะเลในประเทศไทยสวยมาก",
        "Thá-lee nai bprà-thêet Thai sǔay mâak.",
        "The sea in Thailand is very beautiful.",
        "De zee in Thailand is erg mooi.",
      ],
      [
        "watch-stars",
        "ฉันชอบดูดาวตอนกลางคืน",
        "Chǎn châwp duu daao dtawn glaang-kheun.",
        "I like to look at the stars at night.",
        "Ik kijk 's nachts graag naar de sterren.",
      ],
    ],
  },
  {
    slug: "hobbies",
    emoji: "⚽",
    title: {
      en: "Hobbies",
      nl: "Hobby's",
      th: "งานอดิเรก",
    },
    summary: "Talk about favorite sports, music, and active hobbies.",
    vocabulary: [
      ["sport", "กีฬา", "gee-laa", "Sport", "Sport"],
      ["football", "ฟุตบอล", "fút-bawn", "Football / Soccer", "Voetbal"],
      ["swimming", "ว่ายน้ำ", "wâai-náam", "Swimming", "Zwemmen"],
      ["running", "วิ่ง", "wîng", "Running", "Hardlopen"],
      ["music", "ดนตรี", "don-dtree", "Music", "Muziek"],
    ],
    sentences: [
      [
        "favorite-sport",
        "คุณชอบกีฬาอะไรครับ",
        "Khun châwp gee-laa à-rai khráp?",
        "What sport do you like?",
        "Welke sport vindt u leuk?",
      ],
      [
        "swim-morning",
        "ผมว่ายน้ำทุกเช้าครับ",
        "Phǒm wâai-náam thúk cháao khráp.",
        "I swim every morning.",
        "Ik zwem elke ochtend.",
      ],
    ],
  },
  {
    slug: "emergency",
    emoji: "🆘",
    title: {
      en: "Emergency",
      nl: "Noodgevallen",
      th: "เหตุฉุกเฉิน",
    },
    summary: "Recognize essential words for asking for urgent help.",
    vocabulary: [
      ["help", "ช่วยด้วย", "chûay-dûay", "Help! / Please help", "Help! / Help alstublieft"],
      ["fire", "ไฟไหม้", "fai-mâi", "Fire!", "Brand!"],
      ["police", "ตำรวจ", "dtam-rùat", "Police", "Politie"],
      ["ambulance", "รถพยาบาล", "rót phá-yaa-baan", "Ambulance", "Ambulance"],
      ["accident", "อุบัติเหตุ", "ù-bàt-dtì-hèet", "Accident", "Ongeval"],
    ],
    sentences: [
      [
        "please-help",
        "ช่วยด้วยครับ",
        "Chûay-dûay khráp!",
        "Help me, please!",
        "Help me alstublieft!",
      ],
      [
        "call-police",
        "โทรเรียกตำรวจด้วยนะ",
        "Thoo rîak dtam-rùat dûay ná.",
        "Please call the police.",
        "Bel alstublieft de politie.",
      ],
    ],
  },
  {
    slug: "restaurant",
    emoji: "🍽️",
    title: {
      en: "Restaurant",
      nl: "Restaurant",
      th: "ร้านอาหาร",
    },
    summary: "Order politely and ask whether vegetarian food is available.",
    vocabulary: [
      ["order", "สั่ง", "sàng", "Order", "Bestellen"],
      ["bill", "บิล", "bin", "Bill / Check", "Rekening"],
      ["plate", "จาน", "jaan", "Plate / Dish", "Bord / Gerecht"],
      ["spoon", "ช้อน", "cháwn", "Spoon", "Lepel"],
      ["fork", "ส้อม", "sôm", "Fork", "Vork"],
    ],
    sentences: [
      [
        "order-food",
        "ขอสั่งอาหารครับ",
        "Khǎw sàng aa-hǎan khráp.",
        "I'd like to order, please.",
        "Ik wil graag bestellen.",
      ],
      [
        "vegetarian-food",
        "มีอาหารมังสวิรัติไหมครับ",
        "Mee aa-hǎan mang-sà-wí-rát mǎi khráp?",
        "Do you have vegetarian food?",
        "Heeft u vegetarisch eten?",
      ],
    ],
  },
  {
    slug: "drinks",
    emoji: "🧋",
    title: {
      en: "Drinks",
      nl: "Dranken",
      th: "เครื่องดื่ม",
    },
    summary: "Name common family-friendly drinks and place two simple orders.",
    vocabulary: [
      ["plain-water", "น้ำเปล่า", "náam-bplào", "Plain water", "Plat water"],
      ["tea", "ชา", "chaa", "Tea", "Thee"],
      ["coffee", "กาแฟ", "gaa-fae", "Coffee", "Koffie"],
      ["fruit-juice", "น้ำผลไม้", "náam phǒn-lá-mái", "Fruit juice", "Vruchtensap"],
      ["ice", "น้ำแข็ง", "náam-khǎeng", "Ice", "IJs"],
    ],
    sentences: [
      [
        "hot-coffee",
        "ขอกาแฟร้อนหนึ่งแก้วครับ",
        "Khǎw gaa-fae ráwn nèung gâew khráp.",
        "One hot coffee, please.",
        "Eén warme koffie, alstublieft.",
      ],
      [
        "less-sweet-tea",
        "ชาเย็นหวานน้อยครับ",
        "Chaa yen wǎan-nói khráp.",
        "Iced tea, less sweet, please.",
        "IJsthee met minder suiker, alstublieft.",
      ],
    ],
  },
  {
    slug: "communication",
    emoji: "📱",
    title: {
      en: "Communication",
      nl: "Communicatie",
      th: "การสื่อสาร",
    },
    summary: "Use communication vocabulary and ask someone to slow down.",
    vocabulary: [
      ["phone", "โทรศัพท์", "thoo-rá-sàp", "Phone / Telephone", "Telefoon"],
      ["message", "ข้อความ", "khâw-khwaam", "Message / Text", "Bericht / Tekstbericht"],
      ["internet", "อินเทอร์เน็ต", "in-dtêr-nét", "Internet", "Internet"],
      ["app", "แอป", "áep", "App", "App"],
      ["understand", "เข้าใจ", "khâo-jai", "Understand", "Begrijpen"],
    ],
    sentences: [
      [
        "not-understand",
        "ผมไม่เข้าใจครับ",
        "Phǒm mâi khâo-jai khráp.",
        "I don't understand.",
        "Ik begrijp het niet.",
      ],
      [
        "speak-slower",
        "พูดช้าๆ หน่อยได้ไหมครับ",
        "Phûut cháa-cháa nòi dâai mǎi khráp?",
        "Can you speak a little more slowly?",
        "Kunt u iets langzamer spreken?",
      ],
    ],
  },
  {
    slug: "convenience-store",
    emoji: "🏪",
    title: {
      en: "Convenience Store",
      nl: "Buurtwinkel",
      th: "ร้านสะดวกซื้อ",
    },
    summary: "Handle two common requests at a Thai convenience store.",
    vocabulary: [
      ["seven-eleven", "เซเว่น", "see-wen", "7-Eleven", "7-Eleven"],
      ["bag", "ถุง", "thǔng", "Bag", "Tas"],
      ["microwave", "ไมโครเวฟ", "mai-khro-wêp", "Microwave", "Magnetron"],
      ["warm-up", "อุ่น", "ùn", "Warm up / Heat", "Opwarmen"],
      ["frozen", "แช่แข็ง", "châe-khǎeng", "Frozen", "Bevroren"],
    ],
    sentences: [
      [
        "warm-this",
        "ขออุ่นด้วยนะครับ",
        "Khǎw ùn dûay ná khráp.",
        "Please warm this up for me.",
        "Wilt u dit voor mij opwarmen?",
      ],
      [
        "no-bag",
        "ไม่เอาถุงครับ",
        "Mâi ao thǔng khráp.",
        "No bag, thank you.",
        "Geen tas, bedankt.",
      ],
    ],
  },
  {
    slug: "at-the-mall",
    emoji: "🏬",
    title: {
      en: "At the Mall",
      nl: "In het winkelcentrum",
      th: "ที่ห้างสรรพสินค้า",
    },
    summary: "Find your way around a mall and ask about a discount.",
    vocabulary: [
      ["mall", "ห้าง", "hâang", "Mall / Department store", "Winkelcentrum / Warenhuis"],
      ["floor", "ชั้น", "chán", "Floor / Level", "Verdieping"],
      ["elevator", "ลิฟต์", "líft", "Elevator / Lift", "Lift"],
      ["escalator", "บันไดเลื่อน", "ban-dai-lêuan", "Escalator", "Roltrap"],
      ["discount", "ลด", "lót", "Discount / Reduce", "Korting / Verlagen"],
    ],
    sentences: [
      [
        "discount-amount",
        "ลดเท่าไหร่ครับ",
        "Lót thâo-rài khráp?",
        "How much is the discount?",
        "Hoeveel korting is er?",
      ],
      [
        "food-court-ground-floor",
        "โซนอาหารอยู่ชั้นล่างครับ",
        "Soon aa-hǎan yùu chán lâang khráp.",
        "The food court is on the ground floor.",
        "De foodcourt is op de begane grond.",
      ],
    ],
  },
] as const satisfies readonly LessonSeed[];
